import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Seo from "@/components/Seo";
import { adminLogin } from "@/lib/adminAuth";

function formatErr(detail) {
  if (!detail) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail.msg) return detail.msg;
  return String(detail);
}

const AdminLoginPage = () => {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await adminLogin(email, password);
      nav("/admin");
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo titleKey="seo.admin_title" descKey="seo.admin_desc" path="/admin/login" />
      <section className="min-h-[70vh] flex items-center justify-center px-4 py-16">
        <form
          onSubmit={submit}
          className="w-full max-w-md rounded-3xl bg-white border border-slate-200 ring-soft p-8"
          data-testid="admin-login-form"
        >
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50">
            <Lock className="h-5 w-5 text-brand-green" />
          </div>
          <h1 className="mt-4 font-heading font-extrabold tracking-tight text-2xl text-slate-900">
            Accès administrateur
          </h1>
          <p className="mt-1 text-sm text-slate-500">Connectez-vous pour accéder au tableau de bord.</p>
          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="mb-2 block">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password-input"
              />
            </div>
            {err && <div className="text-sm text-red-600" data-testid="admin-login-error">{err}</div>}
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-brand-green hover:bg-brand-green/90"
              data-testid="admin-login-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Se connecter
            </Button>
          </div>
        </form>
      </section>
    </>
  );
};

export default AdminLoginPage;
